import React from 'react';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Grey backdrop over the original page. Blocks interaction with the page
// behind it (applications are closed) but keeps the landing page visible.
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(71, 85, 105, 0.6);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1.5rem;
  animation: ${fadeIn} 0.3s ease-out;
`;

const ModalCard = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 2.5rem 2.25rem;
  max-width: 520px;
  width: 100%;
  text-align: center;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.08);
  animation: ${slideUp} 0.4s ease-out;

  @media (max-width: 480px) {
    padding: 2rem 1.5rem;
    border-radius: 16px;
  }
`;

const IconCircle = styled.div`
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.4rem;
  font-size: 2.2rem;
`;

const Title = styled.h2`
  font-size: 1.6rem;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 0.75rem;
  line-height: 1.3;

  @media (max-width: 480px) {
    font-size: 1.35rem;
  }
`;

const Message = styled.p`
  color: #475569;
  font-size: 1.02rem;
  line-height: 1.7;
  margin-bottom: 0.9rem;
`;

const DeadlineNote = styled.p`
  color: #94a3b8;
  font-size: 0.88rem;
  margin-top: 0.5rem;
`;

const Divider = styled.div`
  width: 64px;
  height: 4px;
  border-radius: 2px;
  background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
  margin: 1.2rem auto 0;
`;

interface DeadlineClosedOverlayProps {
  deadline?: string | null;
}

const DeadlineClosedOverlay: React.FC<DeadlineClosedOverlayProps> = ({ deadline }) => {
  let deadlineText: string | null = null;
  if (deadline) {
    const d = new Date(deadline);
    if (!isNaN(d.getTime())) {
      deadlineText = d.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
  }

  return (
    <Backdrop role="dialog" aria-modal="true" aria-labelledby="deadline-closed-title">
      <ModalCard>
        <IconCircle>&#9203;</IconCircle>
        <Title id="deadline-closed-title">Applications Closed</Title>
        <Message>
          The application deadline has passed and we are now closed for new
          applications. Thank you for your interest in the MNGL DODO CNG
          Dealership Program.
        </Message>
        {deadlineText && (
          <DeadlineNote>Application deadline: {deadlineText}</DeadlineNote>
        )}
        <Divider />
      </ModalCard>
    </Backdrop>
  );
};

export default DeadlineClosedOverlay;
